import { useEffect, useState } from 'react';

/**
 * A page that must not be left by accident (a live broadcast): asked before an in-app link or the
 * back button takes the person elsewhere. Leaving the site is the page's own beforeunload.
 */
let guard: { url: string; mayLeave: () => boolean } | null = null;

/** Ask `mayLeave` before leaving `url`. Returns the function that lifts the guard. */
export function guardLeave(url: string, mayLeave: () => boolean): () => void {
  const g = { url, mayLeave };
  guard = g;
  return () => {
    if (guard === g) guard = null;
  };
}

/** Confirmed (or nothing to confirm): the guarded page is about to unmount, so the guard goes. */
function leaving(): boolean {
  if (!guard) return true;
  if (!guard.mayLeave()) return false;
  guard = null;
  return true;
}

// Registered before any component's listener, so a refused back button never reaches the app.
addEventListener('popstate', (e) => {
  if (!guard || location.pathname + location.search === guard.url) return;
  const { url } = guard;
  if (leaving()) return;
  e.stopImmediatePropagation();
  history.pushState(null, '', url);
});

/** Tiny history router: the app has a handful of routes, a router dependency is not worth it. */
export function navigate(to: string) {
  if (to === location.pathname) return;
  if (!leaving()) return;
  history.pushState(null, '', to);
  dispatchEvent(new PopStateEvent('popstate'));
  window.scrollTo(0, 0);
}

export function usePath(): string {
  const [path, setPath] = useState(location.pathname);
  useEffect(() => {
    const on = () => setPath(location.pathname);
    addEventListener('popstate', on);
    return () => removeEventListener('popstate', on);
  }, []);
  return path;
}
